

import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Button } from "../UI/button";
import { API_SERVER } from "config/constant";
import '../Learner.css';

interface PDFDownloadButtonProps {
  learnerId: string | number;
  gamePlayId?: string;
  reportData?: any;
  htmlString: string;          // ✅ full HTML from generateReportHTML()
  fileName?: string;
}

export function PDFDownloadButton({
  learnerId,
  gamePlayId,
  htmlString,
  fileName = "ECHO-Learner-Report",
}: PDFDownloadButtonProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownload = async () => {
    // Guard: wait until HTML is ready
    if (!htmlString || !learnerId) {
      console.warn("PDF not ready — htmlString or learnerId missing");
      return;
    }

    setIsGenerating(true);

    try {
      const user  = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user?.token;

      // ✅ Send htmlString directly to backend — no URL visit needed
      const response = await fetch(`${API_SERVER}/pdf/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          htmlString,   // ← Puppeteer calls page.setContent(htmlString)
          learnerId,
          gamePlayId,
          fileName,
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`PDF generation failed: ${errText}`);
      }

      const blob = await response.blob();
     

      const url  = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href     = url;
      link.download = `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link); 
      setTimeout(() => URL.revokeObjectURL(url), 3000); 

    } catch (err) {
      console.error("PDF download failed", err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Button
      onClick={handleDownload}
      disabled={isGenerating || !htmlString}   // ✅ disabled until HTML is ready
      className="fixed bottom-6 right-6 z-50"
    >
      {isGenerating ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          Generating...
        </>
      ) : (
        <>
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </>
      )}
    </Button>
  );
}